import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentStorage } from '../../hooks/useDocumentStorage'
import { useAutoSave } from '../../hooks/useAutoSave'
import { useCompanyStore } from '../../store/company/companyStore'
import { useFFEStore } from '../../store/ffeStore'
import { DocumentEditorLayout } from './DocumentEditorLayout'
import { usePageTitle } from '../../utils/pageTitle'
import { message } from 'antd'
import { useApi } from '../../services/ApiContext'
import { FFEDocument } from '../../types/ffe'

const DocumentEditorLayoutMemo = React.memo(DocumentEditorLayout)

export const DocumentEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { loadDocument, saveDocument } = useDocumentStorage()
  const projectTitle = useFFEStore((state) => state.document.projectTitle)
  const setLoading = useFFEStore((state) => state.setLoadingEditor)
  const user = useCompanyStore((state) => state.user)
  const setPurchaseOrders = useFFEStore((state) => state.setPurchaseOrders)
  const setSuppliers = useFFEStore((state) => state.setSuppliers)
  usePageTitle(projectTitle.length > 1 ? projectTitle : 'Specification Editor')
  const api = useApi()
  const [loadedDocument, setLoadedDocument] = useState<FFEDocument | null>(null)
  // Enable auto-save
  useAutoSave(loadedDocument)

  const hasSentSaveMessage = React.useRef(false)
  const hasStartedSetupDev = React.useRef(false)

  const listener = () => {
    if (hasSentSaveMessage.current) return
    hasSentSaveMessage.current = true
    message.open({
      content:
        '🎉 Your changes are saved automatically. You can come back to this page at any time.',
      duration: 8
    })
  }

  useEffect(() => {
    if (user?.isDemo) {
      document.addEventListener('keydown', listener)
    }
    return () => {
      document.removeEventListener('keydown', listener)
    }
  }, [])

  const loadDoc = async () => {
    try {
      setLoading(true)
      if (id) {
        const response = await loadDocument(id)
        if (response) {
          setLoadedDocument(response)
        }
        loadPurchaseOrders(id)
      } else {
        const response = await saveDocument()
        if (response) {
          setLoadedDocument(response)
        }
        if (!response || !response._id) {
          navigate('/app/documents')
          return
        }
        loadPurchaseOrders(response._id)
      }
      loadSuppliers()
    } catch {
      navigate('/app/documents')
    }
    setLoading(false)
  }

  const loadSuppliers = async () => {
    // get all unique suppliers from line items and materials
    const suppliers = await api.findSuppliers({ $limit: 1000 })
    setSuppliers(suppliers.data)
  }

  const loadPurchaseOrders = async (serverId: string) => {
    const purchaseOrders = await api.loadPurchaseOrders(serverId)
    setPurchaseOrders(purchaseOrders)
    return
  }

  // Load document if ID is provided
  useEffect(() => {
    if (!id && hasStartedSetupDev.current) {
      return
    }
    if (!id) {
      hasStartedSetupDev.current = true
    }
    console.log('CLOUD ID:', id)
    loadDoc()
  }, [id])

  return <DocumentEditorLayoutMemo />
}
